<template>
  <div class="body">
    <div class="filter-bar">
      <v-row
        align="center"
        justify="space-between"
        no-gutters>
        <v-col cols="2">
          <h2 class="mb-0">
            Overview
          </h2>
          <span class="subtitle">{{ rows.length }} collections &middot; sorted by sales date</span>
        </v-col>
        <v-col cols="10">
          <v-row
            dense
            justify="end"
            align="center">
            <v-col cols="2">
              <gw-autocomplete
                v-model="filterYear"
                :items="yearOptions"
                label="Year"
                outlined
                dense
                hide-details
                :return-object="false"
                style="width: 110px"
              />
            </v-col>
            <v-col cols="2">
              <gw-autocomplete
                v-model="filterQuarter"
                :items="quarterOptions"
                label="Quarter"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                :return-object="false"
                style="width: 140px"
              />
            </v-col>
            <v-col cols="2">
              <gw-autocomplete
                v-model="filterMonth"
                :items="monthOptions"
                label="Month"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                :return-object="false"
                style="width: 140px"
              />
            </v-col>
            <v-col cols="2">
              <gw-autocomplete
                v-model="filterDesigner"
                :items="designerOptions"
                label="Designer"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details
                clearable
                :return-object="false"
                style="width: 190px"
              />
            </v-col>
            <v-col cols="2">
              <gw-autocomplete
                v-model="filterMerchandiser"
                :items="merchandiserOptions"
                label="Merchandiser"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details
                clearable
                :return-object="false"
                style="width: 190px"
              />
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="primary"
                depressed
                @click="fetchOverview">
                <v-icon left>
                  mdi-magnify
                </v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <div
      v-if="!initLoading && rows.length"
      class="legend-bar">
      <span class="legend-title">Status</span>
      <div
        v-for="item in legend"
        :key="item.label"
        class="legend-item">
        <span
          class="legend-swatch"
          :class="item.style ? item.style.bgClass : 'legend-swatch--empty'"></span>
        <span class="legend-label">{{ item.label }}</span>
      </div>
    </div>
    <div
      v-if="initLoading || tableLoading"
      class="tableContainer d-flex white justify-center align-center">
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <div
      v-else
      class="tableContainer">
      <div
        v-if="!rows.length"
        class="d-flex justify-center align-center"
        style="flex: 1">
        <span class="grey--text">No collections found</span>
      </div>
      <div
        v-else
        class="tableScroll">
        <table>
          <thead>
            <tr>
              <th class="sticky-col">
                Collection
              </th>
              <th
                v-for="stage in stages"
                :key="stage.id"
                class="stage-head">
                <span>{{ stage.name }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id">
              <td class="sticky-col">
                <div class="collection-cell">
                  <span
                    class="avatar"
                    :class="[rowStyle(row).bgClass, rowStyle(row).textColor1]">{{ initialsOf(row.name) }}</span>
                  <div class="collection-meta">
                    <a @click="goToTimeAndAction(row)">{{ row.name }}</a>
                    <span
                      class="meta-line"
                      :title="metaLine(row)">{{ metaLine(row) }}</span>
                  </div>
                </div>
              </td>
              <td
                v-for="stage in stages"
                :key="stage.id"
                class="swatch-cell">
                <span
                  v-if="row.stageSummary[stage.id]"
                  class="swatch"
                  :class="row.stageSummary[stage.id].bgClass"
                  :style="swatchStyle(row.stageSummary[stage.id].status)"
                  :title="row.stageSummary[stage.id].status"></span>
                <span
                  v-else
                  class="swatch swatch--empty"
                  title="No data"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import PrototypeAttributeProvider from '@/resources/PrototypeAttributeProvider'
import calculateTaskStatus from '@/assets/js/CalculateTaskStatus'
import TASK_STATUS_STYLES from '@/assets/js/TaskStatusStyles'

const productionPlanService = new ProductionPlanProvider()
const prototypeAttributeService = new PrototypeAttributeProvider()

const OPEN_SEVERITY = ['critical', 'overdue', 'dueIn0', 'dueIn1', 'dueIn2', 'inProgress', 'pending']
const NO_DATA_STYLE = { bgClass: 'grey lighten-3', textColor1: 'secondary--text' }

export default {
  data () {
    return {
      initLoading: false,
      tableLoading: false,
      stages: [],
      rows: [],
      designerOptions: [],
      merchandiserOptions: [],
      filterYear: new Date().getFullYear(),
      filterQuarter: Math.ceil((new Date().getMonth() + 1) / 3),
      filterMonth: null,
      filterDesigner: null,
      filterMerchandiser: null
    }
  },
  computed: {
    yearOptions () {
      const current = new Date().getFullYear()
      return [current - 1, current, current + 1]
    },
    quarterOptions () {
      return [
        { text: 'Q1 (Jan–Mar)', value: 1 },
        { text: 'Q2 (Apr–Jun)', value: 2 },
        { text: 'Q3 (Jul–Sep)', value: 3 },
        { text: 'Q4 (Oct–Dec)', value: 4 }
      ]
    },
    monthOptions () {
      return [
        { text: 'January', value: 1 },
        { text: 'February', value: 2 },
        { text: 'March', value: 3 },
        { text: 'April', value: 4 },
        { text: 'May', value: 5 },
        { text: 'June', value: 6 },
        { text: 'July', value: 7 },
        { text: 'August', value: 8 },
        { text: 'September', value: 9 },
        { text: 'October', value: 10 },
        { text: 'November', value: 11 },
        { text: 'December', value: 12 }
      ]
    },
    legend () {
      return [
        { style: TASK_STATUS_STYLES.completed_on_time, label: 'Completed' },
        { style: TASK_STATUS_STYLES.critical, label: 'Critical' },
        { style: TASK_STATUS_STYLES.overdue, label: 'Overdue' },
        { style: TASK_STATUS_STYLES.dueIn0, label: 'Due Soon' },
        { style: TASK_STATUS_STYLES.inProgress, label: 'In Progress' },
        { style: TASK_STATUS_STYLES.pending, label: 'Pending' },
        { style: null, label: 'No data' }
      ]
    }
  },
  watch: {
    filterQuarter (val) {
      if (val) this.filterMonth = null
    },
    filterMonth (val) {
      if (val) this.filterQuarter = null
    }
  },
  async mounted () {
    this.initLoading = true
    try {
      await Promise.all([
        this.initSelectOptions(),
        this.fetchWorkflow()
      ])
      await this.fetchOverview()
    } finally {
      this.initLoading = false
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    async fetchWorkflow () {
      const { data } = await productionPlanService.getWorkflow()
      this.stages = (data?.stages ?? [])
        .filter((stage) => stage.status === 'active')
        .sort((stageA, stageB) => stageA.order - stageB.order)
    },
    async initSelectOptions () {
      try {
        const { data } = await prototypeAttributeService.getSquadMember({
          page: 1,
          itemsPerPage: 99999,
          sortBy: ['id'],
          sortDesc: [true]
        })
        this.designerOptions = data.results
          .filter((item) => item.job === 'designer')
          .map((item) => ({ id: item.id, name: item.name }))
        this.merchandiserOptions = data.results
          .filter((item) => item.job === 'merchandiser')
          .map((item) => ({ id: item.id, name: item.name }))
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      }
    },
    async fetchOverview () {
      this.tableLoading = true
      try {
        const { data } = await productionPlanService.getCollectionsOverview({
          year: this.filterYear ?? undefined,
          quarter: this.filterQuarter ?? undefined,
          month: this.filterMonth ?? undefined,
          designer: this.filterDesigner ?? undefined,
          merchandiser: this.filterMerchandiser ?? undefined
        })

        this.rows = (data || []).map((collection) => this.buildOverviewRow(collection, collection.prototype || []))
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.tableLoading = false
      }
    },
    buildOverviewRow (collection, prototypes) {
      const designers = new Map()
      const merchandisers = new Map()
      const entriesByStage = {}

      prototypes.forEach((prototype) => {
        if (prototype.sketch?.designer) {
          designers.set(prototype.sketch.designer.id, prototype.sketch.designer.name)
        }
        if (prototype.sketch?.merchandiser) {
          merchandisers.set(prototype.sketch.merchandiser.id, prototype.sketch.merchandiser.name)
        }
        (prototype.tasks || []).forEach((task) => {
          const style = calculateTaskStatus(task, TASK_STATUS_STYLES)
          if (!entriesByStage[task.stageId]) entriesByStage[task.stageId] = []
          entriesByStage[task.stageId].push({ task, style })
        })
      })

      const stageSummary = {}
      this.stages.forEach((stage) => {
        stageSummary[stage.id] = this.worstStatus(entriesByStage[stage.id] || [])
      })

      return {
        id: collection.id,
        name: collection.name,
        salesDate: collection.salesDate,
        designers: [...designers.values()],
        merchandisers: [...merchandisers.values()],
        sketchCount: prototypes.length,
        stageSummary
      }
    },
    worstStatus (entries) {
      if (!entries.length) return null
      const open = entries.filter((entry) => !String(entry.task.taskStatus || '').startsWith('completed'))
      if (!open.length) return TASK_STATUS_STYLES.completed_on_time
      const worstKey = OPEN_SEVERITY.find((key) => open.some((entry) => entry.style === TASK_STATUS_STYLES[key]))
      return worstKey ? TASK_STATUS_STYLES[worstKey] : open[0].style
    },
    goToTimeAndAction (row) {
      this.$router.push({ name: 'TimeAndAction', params: { collectionId: String(row.id) } })
    },
    rowWorstStyle (row) {
      const summaries = Object.values(row.stageSummary).filter(Boolean)
      if (!summaries.length) return null
      const worstKey = OPEN_SEVERITY.find((key) => summaries.some((summary) => summary === TASK_STATUS_STYLES[key]))
      return worstKey ? TASK_STATUS_STYLES[worstKey] : TASK_STATUS_STYLES.completed_on_time
    },
    rowStyle (row) {
      return this.rowWorstStyle(row) || NO_DATA_STYLE
    },
    initialsOf (name) {
      const letters = name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase())
      return letters.join('')
    },
    metaLine (row) {
      const parts = [this.$options.filters.dateUserFormat(row.salesDate), `${row.sketchCount} sketches`]
      if (row.designers.length) parts.push(row.designers.join(', '))
      if (row.merchandisers.length) parts.push(row.merchandisers.join(', '))
      return parts.join(' · ')
    },
    swatchStyle (status) {
      const accents = {
        'In Progress': '#3f51b5',
        'Due Today': '#ff9800',
        'Due Tomorrow': '#ff9800',
        'Due in 2 Days': '#ff9800',
        'Overdue': '#f44336'
      }
      const color = accents[status]
      if (!color) return {}
      // Vuetify's bgClass utility (e.g. "yellow lighten-4") sets border-color with !important,
      // so the accent ring needs !important too or it's silently overridden.
      return { borderWidth: '1.5px', borderStyle: 'solid', borderColor: `${color} !important` }
    }
  }
}
</script>

<style scoped>
.body {
  flex-direction: column;
  display: flex;
  height: calc(100vh - 70px) !important;
  padding: 10px 10px 0;
  background: #f2f4f5;
}

.filter-bar {
  background-color: #fff;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 8px;
}

.subtitle {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
}

.legend-bar {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 12px 18px;
  background: #1c2b30;
  border-radius: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.legend-title {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
}

.legend-swatch {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
}

.legend-swatch--empty {
  border: 1.5px dashed rgba(255, 255, 255, 0.35);
}

.legend-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.tableContainer {
  flex-grow: 1;
  width: 100%;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0px 3px 3px -2px rgba(0, 0, 0, 0.06), 0px 3px 4px 0px rgba(0, 0, 0, 0.05), 0px 1px 8px 0px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tableScroll {
  overflow: auto;
  flex-grow: 1;
}

table, th, td {
  background-color: #fff;
}

table {
  height: 1px;
  border-collapse: collapse;
  width: 100%;
}

thead tr > th {
  position: sticky;
  padding: 8px;
  text-wrap: nowrap;
  z-index: 2;
  top: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

thead tr > :first-child {
  z-index: 3;
  left: 0;
  top: 0;
}

td {
  text-align: center;
  font-size: 13px;
  color: #3c3e40;
  background-color: #fff;
  height: inherit;
  padding: 8px;
  text-wrap: nowrap;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

tbody tr:hover td {
  background-color: rgb(244, 244, 255);
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  text-align: left;
  min-width: 280px;
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.1);
}

.sticky-col a {
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
  font-size: 14px;
}

.sticky-col a:hover {
  text-decoration: underline;
}

.collection-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.collection-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.meta-line {
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.stage-head {
  vertical-align: bottom;
  min-width: 46px;
}

.stage-head span {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  display: inline-block;
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
}

.swatch-cell {
  padding: 4px 2px !important;
}

.swatch {
  display: inline-flex;
  width: 36px;
  height: 36px;
  border-radius: 8px;
}

.swatch--empty {
  border: 1.5px dashed rgba(0, 0, 0, 0.16);
}
</style>
